import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { API } from "../App";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Search, Receipt, CheckCircle, Clock } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { motion } from "framer-motion";

const formatCurrency = (amount) => {
  return new Intl.NumberFormat('tr-TR', {
    style: 'currency',
    currency: 'TRY',
    minimumFractionDigits: 2
  }).format(amount);
};

const Salaries = () => {
  const [salaries, setSalaries] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedMonth, setSelectedMonth] = useState(new Date().toISOString().slice(0, 7));
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingSalary, setEditingSalary] = useState(null);
  const [formData, setFormData] = useState({
    employee_id: "",
    month: "",
    base_salary: "",
    bonus: "0",
    deductions: "0",
    advance_deduction: "0",
    net_salary: "",
    paid: false,
    paid_date: ""
  });

  const fetchData = useCallback(async () => {
    try {
      const [salariesRes, employeesRes] = await Promise.all([
        axios.get(`${API}/salaries?month=${selectedMonth}`, { withCredentials: true }),
        axios.get(`${API}/employees`, { withCredentials: true })
      ]);
      setSalaries(salariesRes.data);
      setEmployees(employeesRes.data);
    } catch (error) {
      toast.error("Veriler yüklenemedi");
    } finally {
      setLoading(false);
    }
  }, [selectedMonth]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const calculateNetSalary = (base, bonus, deductions, advance) => {
    const b = parseFloat(base) || 0;
    const bo = parseFloat(bonus) || 0;
    const d = parseFloat(deductions) || 0;
    const a = parseFloat(advance) || 0;
    return b + bo - d - a;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const data = {
        ...formData,
        base_salary: parseFloat(formData.base_salary),
        bonus: parseFloat(formData.bonus) || 0,
        deductions: parseFloat(formData.deductions) || 0,
        advance_deduction: parseFloat(formData.advance_deduction) || 0,
        net_salary: calculateNetSalary(formData.base_salary, formData.bonus, formData.deductions, formData.advance_deduction)
      };
      
      if (editingSalary) {
        await axios.put(`${API}/salaries/${editingSalary.id}`, data, { withCredentials: true });
        toast.success("Maaş güncellendi");
      } else {
        await axios.post(`${API}/salaries`, data, { withCredentials: true });
        toast.success("Maaş eklendi");
      }
      setDialogOpen(false);
      resetForm();
      fetchData();
    } catch (error) {
      toast.error("İşlem başarısız");
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Bu maaş kaydını silmek istediğinizden emin misiniz?")) return;
    try {
      await axios.delete(`${API}/salaries/${id}`, { withCredentials: true });
      toast.success("Maaş silindi");
      fetchData();
    } catch (error) {
      toast.error("Silme başarısız");
    }
  };

  const togglePaid = async (salary) => {
    try {
      const data = {
        ...salary,
        paid: !salary.paid,
        paid_date: !salary.paid ? new Date().toISOString().split('T')[0] : null
      };
      await axios.put(`${API}/salaries/${salary.id}`, data, { withCredentials: true });
      toast.success(data.paid ? "Maaş ödendi olarak işaretlendi" : "Maaş beklemede");
      fetchData();
    } catch (error) {
      toast.error("Güncelleme başarısız");
    }
  };

  const openEditDialog = (salary) => {
    setEditingSalary(salary);
    setFormData({
      employee_id: salary.employee_id,
      month: salary.month,
      base_salary: salary.base_salary.toString(),
      bonus: salary.bonus.toString(),
      deductions: salary.deductions.toString(),
      advance_deduction: salary.advance_deduction.toString(),
      net_salary: salary.net_salary.toString(),
      paid: salary.paid,
      paid_date: salary.paid_date || ""
    });
    setDialogOpen(true);
  };

  const openNewSalaryDialog = (employee) => {
    setEditingSalary(null);
    setFormData({
      employee_id: employee?.id || "",
      month: selectedMonth,
      base_salary: employee?.salary?.toString() || "",
      bonus: "0",
      deductions: "0",
      advance_deduction: "0",
      net_salary: employee?.salary?.toString() || "",
      paid: false,
      paid_date: ""
    });
    setDialogOpen(true);
  };

  const resetForm = () => {
    setEditingSalary(null);
    setFormData({
      employee_id: "",
      month: selectedMonth,
      base_salary: "",
      bonus: "0",
      deductions: "0",
      advance_deduction: "0",
      net_salary: "",
      paid: false,
      paid_date: ""
    });
  };

  const totalNet = salaries.reduce((sum, s) => sum + s.net_salary, 0);
  const paidCount = salaries.filter(s => s.paid).length;

  // Get employees without salary for this month
  const employeesWithoutSalary = employees.filter(
    e => !salaries.some(s => s.employee_id === e.id)
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-2 border-amber-400 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="salaries-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-outfit font-semibold text-zinc-50">Maaş Yönetimi</h1>
          <p className="text-zinc-500 text-sm mt-1">
            Toplam: <span className="text-zinc-50">{formatCurrency(totalNet)}</span> | 
            Ödenen: {paidCount}/{salaries.length}
          </p>
        </div>
        <div className="flex gap-2">
          <Input 
            type="month"
            value={selectedMonth}
            onChange={(e) => setSelectedMonth(e.target.value)}
            className="w-40 bg-zinc-900 border-zinc-800"
            data-testid="salary-month-picker"
          />
          <Button 
            onClick={() => openNewSalaryDialog(null)}
            className="bg-amber-400 hover:bg-amber-300 text-zinc-950"
            data-testid="add-salary-btn"
          >
            <Plus className="w-4 h-4 mr-2" />
            Yeni Maaş
          </Button>
        </div>
      </div>

      {/* Quick Add for employees without salary */}
      {employeesWithoutSalary.length > 0 && (
        <div className="bg-amber-400/10 border border-amber-400/20 p-4 rounded-sm">
          <p className="text-sm text-amber-400 mb-3">
            {employeesWithoutSalary.length} personel için bu ay maaş kaydı oluşturulmamış:
          </p>
          <div className="flex flex-wrap gap-2">
            {employeesWithoutSalary.map(e => (
              <Button
                key={e.id}
                variant="outline"
                size="sm"
                onClick={() => openNewSalaryDialog(e)}
                className="border-amber-400/50 text-amber-400 hover:bg-amber-400/20"
                data-testid={`quick-add-salary-${e.id}`}
              >
                <Plus className="w-3 h-3 mr-1" />
                {e.name}
              </Button>
            ))}
          </div>
        </div>
      )}

      {/* Table */}
      <div className="bg-zinc-900 border border-zinc-800 rounded-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-zinc-800">
                <th className="text-left px-4 py-3 text-xs font-medium text-zinc-500 uppercase tracking-wider">Personel</th>
                <th className="text-right px-4 py-3 text-xs font-medium text-zinc-500 uppercase tracking-wider">Taban</th>
                <th className="text-right px-4 py-3 text-xs font-medium text-zinc-500 uppercase tracking-wider">Prim</th>
                <th className="text-right px-4 py-3 text-xs font-medium text-zinc-500 uppercase tracking-wider">Kesinti</th>
                <th className="text-right px-4 py-3 text-xs font-medium text-zinc-500 uppercase tracking-wider">Avans</th>
                <th className="text-right px-4 py-3 text-xs font-medium text-zinc-500 uppercase tracking-wider">Net</th>
                <th className="text-center px-4 py-3 text-xs font-medium text-zinc-500 uppercase tracking-wider">Durum</th>
                <th className="text-right px-4 py-3 text-xs font-medium text-zinc-500 uppercase tracking-wider">İşlemler</th>
              </tr>
            </thead>
            <tbody>
              {salaries.map((salary, index) => (
                <motion.tr 
                  key={salary.id}
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: index * 0.03 }}
                  onClick={() => openEditDialog(salary)}
                  className="border-b border-zinc-800 hover:bg-zinc-800/50 transition-colors cursor-pointer"
                  data-testid={`salary-row-${salary.id}`}
                >
                  <td className="px-4 py-4">
                    <p className="text-sm text-zinc-50">{salary.employee_name}</p>
                  </td>
                  <td className="px-4 py-4 text-right">
                    <span className="text-sm font-mono text-zinc-400">{formatCurrency(salary.base_salary)}</span>
                  </td>
                  <td className="px-4 py-4 text-right">
                    <span className="text-sm font-mono text-emerald-400">+{formatCurrency(salary.bonus)}</span>
                  </td>
                  <td className="px-4 py-4 text-right">
                    <span className="text-sm font-mono text-rose-400">-{formatCurrency(salary.deductions)}</span>
                  </td>
                  <td className="px-4 py-4 text-right">
                    <span className="text-sm font-mono text-rose-400">-{formatCurrency(salary.advance_deduction)}</span>
                  </td>
                  <td className="px-4 py-4 text-right">
                    <span className="text-sm font-mono text-zinc-50 font-medium">{formatCurrency(salary.net_salary)}</span>
                  </td>
                  <td className="px-4 py-4">
                    <button
                      onClick={(e) => { e.stopPropagation(); togglePaid(salary); }}
                      className={`mx-auto flex items-center gap-1 px-2 py-1 rounded-sm text-xs transition-colors ${
                        salary.paid 
                          ? 'bg-emerald-400/10 text-emerald-400 hover:bg-emerald-400/20' 
                          : 'bg-amber-400/10 text-amber-400 hover:bg-amber-400/20'
                      }`}
                      data-testid={`toggle-salary-paid-${salary.id}`}
                    >
                      {salary.paid ? <CheckCircle className="w-3 h-3" /> : <Clock className="w-3 h-3" />}
                      {salary.paid ? 'Ödendi' : 'Bekliyor'}
                    </button>
                  </td>
                  <td className="px-4 py-4">
                    <div className="flex items-center justify-end gap-2">
                      <Pencil className="w-3 h-3 text-zinc-500" />
                      <Button 
                        variant="ghost" 
                        size="icon"
                        onClick={(e) => { e.stopPropagation(); handleDelete(salary.id); }}
                        className="hover:bg-rose-400/10 text-zinc-400 hover:text-rose-400"
                        data-testid={`delete-salary-${salary.id}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </td>
                </motion.tr>
              ))}
              {salaries.length === 0 && (
                <tr>
                  <td colSpan={8} className="px-4 py-8 text-center text-zinc-500">
                    Bu ay için maaş kaydı eklenmemiş
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="bg-zinc-900 border-zinc-800">
          <DialogHeader>
            <DialogTitle className="text-zinc-50 font-outfit">
              {editingSalary ? "Maaş Düzenle" : "Yeni Maaş Kaydı"}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="col-span-2">
                <Label className="text-zinc-400">Personel *</Label>
                <Select 
                  value={formData.employee_id} 
                  onValueChange={(v) => {
                    const emp = employees.find(e => e.id === v);
                    setFormData({ 
                      ...formData, 
                      employee_id: v,
                      base_salary: emp?.salary?.toString() || formData.base_salary
                    });
                  }}
                >
                  <SelectTrigger className="bg-zinc-950 border-zinc-800" data-testid="salary-employee-select">
                    <SelectValue placeholder="Personel seçin" />
                  </SelectTrigger>
                  <SelectContent className="bg-zinc-900 border-zinc-800">
                    {employees.map(e => (
                      <SelectItem key={e.id} value={e.id}>{e.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-zinc-400">Dönem *</Label>
                <Input 
                  type="month"
                  value={formData.month}
                  onChange={(e) => setFormData({ ...formData, month: e.target.value })}
                  required
                  className="bg-zinc-950 border-zinc-800"
                  data-testid="salary-month-input"
                />
              </div>
              <div>
                <Label className="text-zinc-400">Taban Maaş (₺) *</Label>
                <Input 
                  type="number"
                  step="0.01"
                  value={formData.base_salary}
                  onChange={(e) => setFormData({ ...formData, base_salary: e.target.value })}
                  required
                  className="bg-zinc-950 border-zinc-800"
                  data-testid="salary-base-input"
                />
              </div>
              <div>
                <Label className="text-zinc-400">Prim (₺)</Label>
                <Input 
                  type="number"
                  step="0.01"
                  value={formData.bonus}
                  onChange={(e) => setFormData({ ...formData, bonus: e.target.value })}
                  className="bg-zinc-950 border-zinc-800"
                  data-testid="salary-bonus-input"
                />
              </div>
              <div>
                <Label className="text-zinc-400">Kesinti (₺)</Label>
                <Input 
                  type="number"
                  step="0.01"
                  value={formData.deductions}
                  onChange={(e) => setFormData({ ...formData, deductions: e.target.value })}
                  className="bg-zinc-950 border-zinc-800"
                  data-testid="salary-deductions-input"
                />
              </div>
              <div>
                <Label className="text-zinc-400">Avans Kesintisi (₺)</Label>
                <Input 
                  type="number"
                  step="0.01"
                  value={formData.advance_deduction}
                  onChange={(e) => setFormData({ ...formData, advance_deduction: e.target.value })}
                  className="bg-zinc-950 border-zinc-800"
                  data-testid="salary-advance-input"
                />
              </div>
              <div>
                <Label className="text-zinc-400">Ödeme Tarihi</Label>
                <Input 
                  type="date"
                  value={formData.paid_date}
                  onChange={(e) => setFormData({ ...formData, paid_date: e.target.value, paid: !!e.target.value })}
                  className="bg-zinc-950 border-zinc-800"
                  data-testid="salary-paiddate-input"
                />
              </div>
            </div>
            <div className="p-3 bg-zinc-950 border border-zinc-800 rounded-sm">
              <p className="text-sm text-zinc-500">Net Maaş: 
                <span className="text-lg font-mono text-amber-400 ml-2">
                  {formatCurrency(calculateNetSalary(formData.base_salary, formData.bonus, formData.deductions, formData.advance_deduction))}
                </span>
              </p>
            </div>
            <DialogFooter>
              <Button 
                type="button" 
                variant="outline" 
                onClick={() => setDialogOpen(false)}
                className="border-zinc-700 hover:bg-zinc-800"
              >
                İptal
              </Button>
              <Button 
                type="submit"
                className="bg-amber-400 hover:bg-amber-300 text-zinc-950"
                data-testid="save-salary-btn"
              >
                {editingSalary ? "Güncelle" : "Kaydet"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Salaries;
